/* eslint-disable id-length */
import { useEffect } from "react";
import { TooltipModel } from "chart.js";
import classNames from "classnames";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "app/components/shared/HoverCard";
import { useChart } from "app/components/shared/Chart/chart";

import LineChart, { LineChartUnit } from "../LineChart";
import { LegendIndicator } from "../LegendIndicator";
import { TimePeriod } from "../Renderer";
import { useToggleDatasets, useHighlightDataset } from "./hooks";
import {
  ThemeNegativeTrend,
  ThemeDefaultTrend,
  colorsForIndex,
} from "../Theme";
import {
  Card,
  CardSection,
  CardChart,
  CardLabel,
  CardDelta,
  CardStat,
  CardIcon,
} from "../Card";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "../Table";
import SparkLineChart, {
  withDashedLine,
  withGradientFillLine,
} from "../SparkLineChart";
import {
  calculateAverage,
  calculateMin,
  calculateMax,
  formatDuration,
  formatTooltipDateString,
  getTimePeriodString,
} from "./utils";

export function QueueWaitTimeSpark({
  datasets,
  p95,
}: {
  datasets: {
    current: { x: number; y: number }[];
    previous: { x: number; y: number }[];
  };
  p95: number;
}) {
  const currentAvg = calculateAverage(datasets.current);
  const previousAvg = calculateAverage(datasets.previous);

  const delta = currentAvg - previousAvg;
  const theme = delta > 0 ? ThemeNegativeTrend : ThemeDefaultTrend;

  const formattedp50 = formatDuration(currentAvg);
  const formattedp95 = formatDuration(p95);
  const formattedDelta = formatDuration(Math.abs(delta));

  return (
    <Card>
      <CardSection>
        <CardLabel>Queue wait time</CardLabel>
        <CardDelta
          formattedValue={formattedDelta}
          value={delta}
          color={theme.color}
        />
      </CardSection>

      <CardSection>
        <CardStat value={formattedp50} label="Avg" size="lg" layout="stacked" />
        <CardStat value={formattedp95} label="p95" />
      </CardSection>

      <CardChart>
        <SparkLineChart
          data={{
            labels: datasets.current.map(({ x }) => x),
            datasets: [
              withGradientFillLine(
                datasets.current.map(({ y }) => y),
                theme,
              ),
              withDashedLine(datasets.previous.map(({ y }) => y)),
            ],
          }}
        />
      </CardChart>
    </Card>
  );
}

export function QueueWaitTimeDetail({
  dataset,
  timePeriod,
}: {
  timePeriod: TimePeriod;
  dataset: {
    label: string;
    delta: number;
    data: { x: number; y: number }[];
  }[];
}) {
  const { chartRef, updateData } = useChart();
  const { isDatasetVisible, visibilityHandlers } = useToggleDatasets(chartRef);
  const highlightHandlers = useHighlightDataset(chartRef);

  const coloredDatasets = dataset.map((data, index) => ({
    ...data,
    ...colorsForIndex(index),
  }));

  useEffect(() => {
    updateData({ datasets: dataset });
  }, [dataset, updateData]);

  return (
    <Card className="lg:flex-row">
      <div className="flex flex-col grow gap-3 min-w-0">
        <CardSection className="justify-start">
          <CardIcon color="#2664EC" />
          <CardLabel
            description={`${getTimePeriodString(timePeriod)} • Average wait`}
          >
            Queue wait time
          </CardLabel>
        </CardSection>

        <CardChart>
          <LineChart
            data={{ datasets: coloredDatasets }}
            ref={chartRef}
            tooltip={Tooltip}
            units={{ y: LineChartUnit.duration }}
          />
        </CardChart>
      </div>

      <Table className="lg:basis-96">
        <TableHead>
          <TableRow>
            <TableHeader>Metric</TableHeader>
            <TableHeader>Avg</TableHeader>
            <TableHeader className="text-right">Min</TableHeader>
            <TableHeader className="text-right">Max</TableHeader>
          </TableRow>
        </TableHead>

        <TableBody>
          {coloredDatasets.map(({ data, delta, label, borderColor }, index) => {
            const avg = formatDuration(calculateAverage(data));
            const min = formatDuration(calculateMin(data));
            const max = formatDuration(calculateMax(data));

            const theme = delta > 0 ? ThemeNegativeTrend : ThemeDefaultTrend;
            const formattedDelta = formatDuration(Math.abs(delta));
            const visible = isDatasetVisible(index);

            return (
              <TableRow
                key={label}
                className={classNames({
                  "text-slate-500": !visible,
                })}
                data-visible={visible}
                {...visibilityHandlers(index)}
                {...highlightHandlers(index)}
              >
                <TableCell
                  className="font-semibold truncate max-w-0 w-2/5"
                  title={label}
                >
                  <LegendIndicator
                    color={borderColor}
                    className="mr-1.5"
                    selected={visible}
                  />
                  {label}
                </TableCell>

                <TableCell>
                  {avg}
                  <CardDelta
                    className="ml-1 text-[11px]"
                    formattedValue={formattedDelta}
                    value={delta}
                    color={visible ? theme.color : "#c2cace"}
                  />
                </TableCell>

                <TableCell className="text-right">{min}</TableCell>
                <TableCell className="text-right">{max}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

const Tooltip = ({ tooltip }: { tooltip: TooltipModel<"line"> }) => {
  const dateString = formatTooltipDateString(tooltip.dataPoints[0].parsed.x);

  return (
    <HoverCard open={true}>
      <HoverCardTrigger />
      <HoverCardContent side="right" className="w-auto px-2 py-2.5">
        <div className="flex flex-col gap-1.5">
          <div className="font-semibold text-xs">{dateString}</div>

          {tooltip.dataPoints.map(({ dataset, parsed }, index) => {
            return (
              <div
                key={index}
                className="font-semibold text-xs"
                style={{ color: dataset.borderColor as string }}
              >
                {dataset.label}:{" "}
                <span className="font-medium text-charcoal-300">
                  {formatDuration(parsed.y)}
                </span>
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
